import { Grid, InputAdornment } from "@mui/material";
import dayjs from "dayjs";
import _ from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../framework/Button";
import Checkbox from "../../framework/Checkbox";
import ChipInputSelect from "../../framework/ChipInputSelect";
import DatePicker from "../../framework/date/DatePicker";
import GlobalContext from "../../framework/GlobalContext";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { displayToast } from "../../framework/toast/toastUtils";
import { listBranchGroup } from "../../services/branch-group-services";
import { fetchBranches } from "../../services/branch-service";
import { addDiscountProgram } from "../../services/discount-program";
import { listEntity } from "../../services/entity-services";
import { focusCell } from "../../utils/gridUtils";
import classes from "./DiscountProgramActors.module.css";
import { initial_state_discount_program } from "./DiscountProgramConstants";
import DiscountProgramContext from "./DiscountProgramContext";
import {
  createDiscountProgramFormData,
  createDiscountProgramItemFormData,
} from "./DiscountProgramUtils";
const DiscountProgramActors = (props) => {
  const { setCurrentComponent, setShowLoading, showLoading } =
    useContext(GlobalContext);
  const {
    discountProgramRef,
    discountProgramStartDateRef,
    saveDiscountRef,
    discountProgramGridRef,
    discountProgramEndDateRef,
  } = useContext(DiscountProgramContext);
  const branchRef = useRef("");
  const entityRef = useRef(null);
  const branchResetKeyRef = useRef(0);
  const [branches, setBranches] = useState([]);
  const [entities, setEntities] = useState([]);
  const dispatch = useDispatch();
  const {
    discountProgramRowData,
    actors: { name, start, end, discountProgramCode, branch, entity },
    gridColumns,
  } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );
  const resetDiscountProgram = useCallback(() => {
    branchResetKeyRef.current += 1;
    const newDiscountProgram = {
      category: "",
      option: "",
      min: 0,
      max: 0,
      discPerDefault: 0,
    };
    const newDiscountProgramData = [newDiscountProgram];
    dispatch({
      type: "reset_active_tab",
      payload: { ...initial_state_discount_program, gridColumns, discountProgramRowData: newDiscountProgramData },
    });

    setTimeout(() => {
      focusCell(
        discountProgramGridRef.current.api,
        0,
        discountProgramGridRef.current.columnApi.getColumn("category")
      );
    }, 100);
  }, [discountProgramGridRef, dispatch, gridColumns]);

  const updateActorStates = (payload) => {
    dispatch({ type: "update_discount_program_actors", payload });
  };
  const debounceUpdateActors = _.debounce(updateActorStates, 1);

  const fetchBranchesWithBranchGroup = useCallback(async () => {
    try {
      const _branch = await fetchBranches();
      const _branchGroup = await listBranchGroup();
      const mergedBranches = [
        ...(_branchGroup?.result?.rows || []),
        ...(_branch?.result?.rows || []),
      ];
      setBranches(mergedBranches || []);
      dispatch({
        type: "update_discount_program_actors",
        payload: {
          allBranches: mergedBranches || [],
        },
      });
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [dispatch]);

  const fetchEntities = useCallback(async () => {
    try {
      const resp = await listEntity();
      setEntities(resp?.result?.rows || []);
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  }, []);

  const saveNewDiscountProgram = useCallback(async () => {
    setShowLoading(true);
    let saveDiscountItems = [];
    let saveDiscountProgramData = {};
    discountProgramRowData.forEach((item) => {
      saveDiscountItems.push(
        createDiscountProgramItemFormData(item, false, entity?.entityId)
      );
    });
    const selectedBranchIds = (branch || []).reduce((acc, b) => {
      if (b?.branchCode) {
        acc.push(...b.branchCode);
      } else if (b?.code) {
        acc.push(b.code);
      }
      return acc;
    }, []);
    const uniqueBranchIds = [...new Set(selectedBranchIds)];
    saveDiscountProgramData = createDiscountProgramFormData({
      programName: name,
      startDate: start,
      endDate: end,
      discountProgramDetails: saveDiscountItems,
    });
    const resp = await addDiscountProgram({
      ...saveDiscountProgramData,
      branchCode: uniqueBranchIds,
      ...(discountProgramCode && { discountProgramCode }),
    });
    if (resp?.status === "success") {
      displayToast({ type: "success", title: "Discount Program Saved." });
      resetDiscountProgram();
    }
    setShowLoading(false);
  }, [
    setShowLoading,
    discountProgramRowData,
    branch,
    name,
    start,
    end,
    discountProgramCode,
    entity?.entityId,
    resetDiscountProgram,
  ]);

  const validateForm = useCallback(() => {
    let isValid = true;
    if (!start || start.length < 10) {
      displayToast({
        type: "error",
        title: "Please enter valid start date",
      });
      discountProgramStartDateRef.current.focus({ preventScroll: false });
      return false;
    }
    if (!name) {
      displayToast({
        type: "error",
        title: "Please enter discount program name.",
      });
      discountProgramRef.current.focus({ preventScroll: false });
      return false;
    }

    if (start && end && dayjs(start, "DD/MM/YYYY") > dayjs(end, "DD/MM/YYYY")) {
      displayToast({
        type: "error",
        title: "End date cannot be earlier than start date",
      });
      discountProgramEndDateRef.current.focus({ preventScroll: false });
      return false;
    }

    if (!entity || !entity?.entityId) {
      displayToast({
        type: "error",
        title: "Please select entity.",
      });
      try {
        entityRef.current?.focus?.();
      } catch (_) {}
      return false;
    }

    if (!Array.isArray(branch) || branch.length === 0) {
      displayToast({
        type: "error",
        title: "Please select at least one branch.",
      });
      try {
        branchRef.current?.focus?.();
      } catch (_) {}
      return false;
    }

    // Group slabs by option to validate continuous ranges
    const slabsByOption = {};
    discountProgramRowData.forEach((row, index) => {
      const { category, option, min, max, discPerDefault } = row;

      if (
        !category ||
        !option ||
        typeof min !== "number" ||
        typeof max !== "number" ||
        typeof discPerDefault !== "number"
      ) {
        displayToast({
          type: "error",
          title: "Please enter valid category, option, min, max and discount.",
        });
        focusCell(
          discountProgramGridRef.current.api,
          index,
          discountProgramGridRef.current.columnApi.getColumn("min")
        );
        isValid = false;
        return;
      }
      if (max <= min) {
        displayToast({
          type: "error",
          title: "Max value must be greater than min value.",
        });
        focusCell(
          discountProgramGridRef.current.api,
          index,
          discountProgramGridRef.current.columnApi.getColumn("max")
        );
        isValid = false;
        return;
      }

      // Group by option for continuous range validation
      let optionId = option?.categoryId || option?.value?.categoryId;
      if (category?.id === "manufacturer") {
        optionId = option?.id || option?.manufacturerId;
      } else if (category?.id === "manufacturer_group") {
        optionId = option?.id || option?.manufacturerGroupId;
      }
      if (!slabsByOption[optionId]) {
        slabsByOption[optionId] = [];
      }
      slabsByOption[optionId].push({ ...row, originalIndex: index });
    });

    // Validate continuous ranges for each option
    Object.keys(slabsByOption).forEach((optionId) => {
      const slabs = slabsByOption[optionId].sort((a, b) => a.min - b.min);

      for (let i = 1; i < slabs.length; i++) {
        const prevSlab = slabs[i - 1];
        const currentSlab = slabs[i];

        if (currentSlab.min !== prevSlab.max + 1) {
          displayToast({
            type: "error",
            title: `Min value should be (previous slab's max + 1) for option: ${
              currentSlab.option.categoryName ||
              currentSlab.option.label ||
              currentSlab.option.value?.categoryName ||
              currentSlab.option.value?.label ||
              ""
            }`,
          });
          focusCell(
            discountProgramGridRef.current.api,
            currentSlab.originalIndex,
            discountProgramGridRef.current.columnApi.getColumn("min")
          );
          isValid = false;
          return;
        }
      }
    });

    return isValid;
  }, [
    start,
    end,
    name,
    discountProgramRowData,
    discountProgramStartDateRef,
    discountProgramEndDateRef,
    discountProgramRef,
    discountProgramGridRef,
    branch,
    entity,
  ]);

  const saveClickHandler = useCallback(() => {
    if (validateForm()) {
      saveNewDiscountProgram();
    }
  }, [saveNewDiscountProgram, validateForm]);

  useEffect(() => {
    discountProgramRef.current.value = name;
  }, [name, discountProgramRef]);

  useEffect(() => {
    fetchBranchesWithBranchGroup();
  }, [fetchBranchesWithBranchGroup]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  return (
    <>
      <div style={{ height: "100%", padding: "5px 0", width: "100%" }}>
        <Grid sx={{ marginTop: 0 }} container spacing={1.25} columns={12}>
          <Grid item xs={2.5}>
            <DatePicker
              id="pp_date_picker_discount_program_start_date"
              ref={discountProgramStartDateRef}
              label="Start Date"
              disablePast={true}
              value={dayjs(start, "DD/MM/YYYY")}
              onFocus={() =>
                setCurrentComponent(
                  "pp_date_picker_discount_program_start_date"
                )
              }
              onChange={(value, { validationError }) => {
                if (!value) {
                  debounceUpdateActors({
                    start: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                if (validationError === "disablePast") {
                  displayToast({
                    type: "error",
                    title: "Past date is not allowed",
                  });
                  debounceUpdateActors({
                    start: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                debounceUpdateActors({ start: value.format("DD/MM/YYYY") });
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  altUp: "grid_discount_program",
                  up: "grid_discount_program",
                  left: "discount_program_name",
                  right: "pp_date_picker_discount_program_end_date",
                }),
              }}
            />
          </Grid>
          <Grid item xs={2.5}>
            <DatePicker
              id="pp_date_picker_discount_program_end_date"
              inputRef={discountProgramEndDateRef}
              label="End Date"
              disablePast={true}
              value={dayjs(end, "DD/MM/YYYY")}
              onFocus={() =>
                setCurrentComponent("pp_date_picker_discount_program_end_date")
              }
              onChange={(value, { validationError }) => {
                if (!value) {
                  debounceUpdateActors({
                    end: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                if (validationError === "disablePast") {
                  displayToast({
                    type: "error",
                    title: "Past date is not allowed",
                  });
                  debounceUpdateActors({
                    end: dayjs().format("DD/MM/YYYY"),
                  });
                  return;
                }
                debounceUpdateActors({ end: value.format("DD/MM/YYYY") });
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  altUp: "grid_discount_program",
                  up: "grid_discount_program",
                  left: "pp_date_picker_discount_program_start_date",
                  right: "discount_program_name",
                }),
              }}
            />
          </Grid>
          <Grid item xs={3.5}>
            <TextInput
              id="discount_program_name"
              label="Program Name"
              ref={discountProgramRef}
              placeholder="Type here..."
              value={name}
              required={true}
              onFocus={() => {
                setCurrentComponent("discount_program_name");
              }}
              onChange={(e) => {
                debounceUpdateActors({ name: e.target.value });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ padding: "0 5px 0 0" }}>
                    <Shortcut keys={["Alt", "N"]} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "data-markers": JSON.stringify({
                  altUp: "grid_discount_program",
                  up: "grid_discount_program",
                  down: "grid_discount_program",
                  right: "discount_program_entity",
                  left: "pp_date_picker_discount_program_end_date",
                }),
              }}
            />
          </Grid>
          <Grid item xs={3.5}>
            <SelectInput
              id="discount_program_entity"
              label="Entity"
              ref={entityRef}
              key={entity}
              options={entities}
              value={entity}
              required={true}
              getOptionLabel={(option) =>
                option?.entityName || option?.label || ""
              }
              isOptionEqualToValue={(option, value) =>
                option?.entityId === value?.entityId
              }
              onFocus={() => {
                setCurrentComponent("discount_program_entity");
              }}
              onChange={(event, option) => {
                debounceUpdateActors({ entity: option });
              }}
              markers={JSON.stringify({
                up: "pp_date_picker_discount_program_end_date",
                left: "discount_program_name",
                right: "discount_program_branch",
                down: "discount_program_branch",
              })}
            />
          </Grid>
        </Grid>
        <Grid sx={{ marginTop: 1 }} container spacing={1} columns={12}>
          <Grid item xs={10}>
            <ChipInputSelect
              id="discount_program_branch"
              value={branch || []}
              options={branches}
              key={branchResetKeyRef.current}
              groupBy={(option) => (option?.code ? "Branches" : "Branch Group")}
              name="branch"
              required={true}
              ref={branchRef}
              getOptionLabel={(option) =>
                `${option?.name || ""} (${option?.code || "BG"})`
              }
              isOptionEqualToValue={(option, value) => {
                return option === value;
              }}
              onFocus={() => {
                setCurrentComponent("discount_program_branch");
              }}
              onChange={(event, option) => {
                debounceUpdateActors({ branch: option });
              }}
              openOnFocus={true}
              label="Branches"
              multiple={true}
              blurOnSelect={false}
              disableCloseOnSelect={true}
              limitTags={3}
              sx={{
                "& .MuiAutocomplete-input": {
                  minHeight: "40px",
                },
              }}
              renderGroup={(params) => {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      padding: "5px",
                      gap: "5px",
                    }}
                    key={params.key}
                  >
                    <div
                      style={{
                        height: "30px",
                        width: "100%",
                        background: "#e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        padding: "5px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {params.group}
                    </div>
                    <div>{params.children}</div>
                  </div>
                );
              }}
              renderOption={(props, option, { selected }) => {
                return (
                  <div
                    {...props}
                    style={{
                      display: "flex",
                      height: "40px",
                      width: "100%",
                      padding: "1px 5px",
                      gap: "5px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItem: "center",
                      }}
                    >
                      <Checkbox checked={selected} />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItem: "center",
                        gap: "5px",
                      }}
                    >
                      <div
                        style={{
                          height: "15px",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        {option?.name} ({option?.code || "BG"})
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {option?.address}
                      </div>
                    </div>
                  </div>
                );
              }}
              markers={JSON.stringify({
                up: "discount_program_entity",
                left: "discount_program_entity",
                right: "discount_program_branch",
                down: "discount_program_branch",
              })}
            />
          </Grid>
          <Grid
            item
            xs={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className={classes["actors-button-wrapper"]}>
              <Button
                label={"Save"}
                ref={saveDiscountRef}
                color="violet"
                disabled={showLoading}
                endIcon={<Shortcut keys={["Ctrl", "S"]} />}
                onClick={saveClickHandler}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default DiscountProgramActors;
